import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { RefreshCw, Download, AlertTriangle, MapPin, Users, ChevronDown, ChevronRight, MessageCircle, Phone } from "lucide-react";

/** Normalisasi nomor HP Indonesia ke format wa.me (628xxx). */
function normalizeWaNumber(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("62")) return digits;
  if (digits.startsWith("0")) return "62" + digits.slice(1);
  if (digits.startsWith("8")) return "62" + digits;
  return digits;
}

/**
 * Pemetaan TPS (read-only) — peta wilayah kerja PPDP per TPS.
 * Data diturunkan otomatis dari database (voters, tps, users PPDP).
 * Fungsi utama:
 *   1. Referensi wilayah kerja PPDP per TPS (RW/RT tercakup + jumlah pemilih L/P)
 *   2. Monitoring TPS mana yang belum ada PPDP-nya
 *   3. Hubungi PPDP langsung via WhatsApp
 */
export default function PpdpMappingSimulationPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [dusunFilter, setDusunFilter] = useState("all");
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/ppdp-mapping/simulate");
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dusuns = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.rows.map((r) => r.dusun))].sort();
  }, [data]);

  const rows = useMemo(() => {
    if (!data) return [];
    const s = q.trim().toLowerCase();
    return data.rows.filter((r) => {
      if (dusunFilter !== "all" && r.dusun !== dusunFilter) return false;
      if (!s) return true;
      return (
        r.tps_kode.toLowerCase().includes(s) ||
        r.dusun.toLowerCase().includes(s) ||
        (r.tps_nama || "").toLowerCase().includes(s)
      );
    });
  }, [data, q, dusunFilter]);

  const downloadCsv = () => {
    if (!data) return;
    const header = ["Dusun", "TPS Kode", "TPS Nama", "Total Pemilih", "Laki-laki", "Perempuan", "Jumlah RW", "Jumlah RT", "Rincian RW/RT", "PPDP Saat Ini", "Anomali"];
    const lines = [header.join(",")];
    for (const r of data.rows) {
      const rincian = r.rw_breakdown
        .map((rw) => `RW ${rw.rw}: RT[${rw.rts.map((rt) => `${rt.rt}(${rt.jumlah})`).join(";")}]`)
        .join(" | ");
      const ppdp = r.matching_ppdp.map((p) => `${p.username}(${p.name})`).join(";") || "-";
      const anomali = r.anomalies.map((a) => `${a.reason}[RW${a.rw}/RT${a.rt}:${a.count}]`).join(";") || "-";
      lines.push([
        r.dusun, r.tps_kode, r.tps_nama, r.total_pemilih, r.laki_laki || 0, r.perempuan || 0, r.jumlah_rw, r.jumlah_rt,
        `"${rincian}"`, `"${ppdp}"`, `"${anomali}"`,
      ].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `simulasi-ppdp-tps-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) {
    return (
      <div className="text-sm text-slate-500 p-6" data-testid="ppdp-simulation-loading">
        {loading ? "Memuat simulasi pemetaan..." : "Tidak ada data"}
      </div>
    );
  }

  const s = data.summary;

  return (
    <div className="space-y-6" data-testid="ppdp-mapping-simulation-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Referensi</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">
            Pemetaan TPS
          </h1>
          <p className="text-slate-600 text-sm mt-2 max-w-2xl">
            Peta wilayah kerja PPDP per TPS. Rincian RW/RT & jumlah pemilih diturunkan otomatis dari database.
            <span className="block text-xs text-slate-400 mt-1">Untuk mengelola akun & area tugas PPDP, buka menu <b>Pengguna</b>.</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="btn-refresh-simulation">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={downloadCsv} data-testid="btn-download-csv">
            <Download className="h-4 w-4 mr-2" /> Unduh CSV
          </Button>
        </div>
      </header>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card><CardContent className="pt-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Total TPS</div>
          <div className="text-3xl font-bold tabular-nums" data-testid="kpi-total-tps">{s.total_tps}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Total Pemilih</div>
          <div className="text-3xl font-bold tabular-nums" data-testid="kpi-total-pemilih">{s.total_pemilih.toLocaleString("id-ID")}</div>
          <div className="mt-1 flex gap-2 text-[11px] font-semibold">
            <span className="rounded bg-blue-100 text-blue-800 px-1.5 py-0.5" data-testid="kpi-lk">L {(s.total_laki_laki || 0).toLocaleString("id-ID")}</span>
            <span className="rounded bg-pink-100 text-pink-800 px-1.5 py-0.5" data-testid="kpi-pr">P {(s.total_perempuan || 0).toLocaleString("id-ID")}</span>
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">PPDP Aktif</div>
          <div className="text-3xl font-bold tabular-nums" data-testid="kpi-total-ppdp">{s.total_ppdp_users}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold text-amber-600">TPS tanpa PPDP</div>
          <div className="text-3xl font-bold tabular-nums text-amber-700" data-testid="kpi-tps-tanpa-ppdp">{s.tps_tanpa_ppdp.length}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold text-red-600">TPS Anomali</div>
          <div className="text-3xl font-bold tabular-nums text-red-700" data-testid="kpi-tps-anomali">{s.tps_dengan_anomali.length}</div>
        </CardContent></Card>
      </div>

      {/* Warnings */}
      {(s.tps_dengan_anomali.length > 0 || s.ppdp_orphan.length > 0 || s.tps_multi_ppdp_count > 0) && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-5 space-y-2">
            <div className="flex items-center gap-2 text-amber-800 font-semibold">
              <AlertTriangle className="h-4 w-4" /> Peringatan / Anomali
            </div>
            {s.tps_dengan_anomali.length > 0 && (
              <div className="text-sm text-amber-900">
                <b>{s.tps_dengan_anomali.length}</b> TPS memiliki pemilih dengan RW/RT kosong atau &quot;000&quot;: {s.tps_dengan_anomali.join(", ")}
              </div>
            )}
            {s.tps_multi_ppdp_count > 0 && (
              <div className="text-sm text-amber-900">
                <b>{s.tps_multi_ppdp_count}</b> TPS ter-cover oleh lebih dari 1 PPDP. Setelah migrasi, tetapkan 1 PPDP per TPS.
              </div>
            )}
            {s.ppdp_orphan.length > 0 && (
              <div className="text-sm text-amber-900">
                <b>{s.ppdp_orphan.length}</b> PPDP tidak terhubung ke TPS mana pun (area tugas saat ini tidak match): {s.ppdp_orphan.map((p) => p.username).join(", ")}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cari kode/nama TPS..."
          className="max-w-xs"
          data-testid="input-search-tps"
        />
        <select
          value={dusunFilter}
          onChange={(e) => setDusunFilter(e.target.value)}
          className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white"
          data-testid="select-dusun-filter"
        >
          <option value="all">Semua Dusun</option>
          {dusuns.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <div className="text-xs text-slate-500 ml-auto">
          Menampilkan <b>{rows.length}</b> dari <b>{data.rows.length}</b> TPS
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="p-3 text-left w-6"></th>
              <th className="p-3 text-left">TPS</th>
              <th className="p-3 text-left">Dusun</th>
              <th className="p-3 text-left">Wilayah Kerja (RW/RT)</th>
              <th className="p-3 text-right">Pemilih</th>
              <th className="p-3 text-left">PPDP</th>
              <th className="p-3 text-center">Kontak</th>
              <th className="p-3 text-center">Status</th>
            </tr>
          </thead>
          <tbody data-testid="tps-simulation-tbody">
            {rows.map((r) => {
              const key = `${r.dusun}|${r.tps_kode}`;
              const isOpen = !!expanded[key];
              const hasAnomaly = r.anomalies.length > 0;
              const noPpdp = r.ppdp_count === 0 && r.total_pemilih > 0;
              return (
                <>
                  <tr
                    key={key}
                    className={`border-t border-slate-100 ${hasAnomaly ? "bg-red-50/40" : noPpdp ? "bg-amber-50/40" : ""}`}
                    data-testid={`tps-row-${r.tps_kode}`}
                  >
                    <td className="p-3">
                      <button
                        onClick={() => setExpanded((e) => ({ ...e, [key]: !isOpen }))}
                        className="text-slate-500 hover:text-slate-900"
                        data-testid={`toggle-${r.tps_kode}`}
                        aria-label="Detail"
                      >
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="p-3 font-semibold tabular-nums">{r.tps_kode}</td>
                    <td className="p-3 text-slate-700">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-slate-400" />
                        {r.dusun || <span className="text-slate-400 italic">—</span>}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {r.rw_breakdown.length === 0 && (
                          <span className="text-slate-400 italic text-xs">Belum ada pemilih</span>
                        )}
                        {r.rw_breakdown.map((rw) => (
                          <span
                            key={rw.rw}
                            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700"
                          >
                            RW {rw.rw}
                            <span className="text-slate-400">•</span>
                            <span className="text-slate-500">RT {rw.rts.map((rt) => rt.rt).join(",")}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-3 text-right tabular-nums font-medium">
                      <div className="flex items-center justify-end gap-1">
                        <Users className="h-3 w-3 text-slate-400" />
                        {r.total_pemilih.toLocaleString("id-ID")}
                      </div>
                      <div className="flex justify-end gap-1 mt-1 text-[10px] font-semibold">
                        <span className="rounded bg-blue-50 text-blue-700 px-1">L {(r.laki_laki || 0).toLocaleString("id-ID")}</span>
                        <span className="rounded bg-pink-50 text-pink-700 px-1">P {(r.perempuan || 0).toLocaleString("id-ID")}</span>
                      </div>
                    </td>
                    <td className="p-3">
                      {r.matching_ppdp.length === 0 ? (
                        <span className="text-xs text-amber-600">— Belum ada PPDP</span>
                      ) : (
                        <div className="space-y-0.5">
                          {r.matching_ppdp.map((p) => (
                            <div key={p.id} className="text-xs">
                              <div className="font-medium">{p.name}</div>
                              <div className="text-slate-400">@{p.username}{p.no_hp ? ` · ${p.no_hp}` : ""}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      {r.matching_ppdp.length === 0 ? (
                        <span className="text-slate-300 text-xs">—</span>
                      ) : (
                        <div className="flex items-center justify-center gap-1.5 flex-wrap">
                          {r.matching_ppdp.map((p) => {
                            const waNumber = normalizeWaNumber(p.no_hp);
                            const waMsg = encodeURIComponent(
                              `Halo ${p.name},\nSaya dari panitia Pilkades. Terkait tugas Coklit di ${r.tps_kode} (${r.dusun}).`
                            );
                            return waNumber ? (
                              <div key={p.id} className="flex items-center gap-1">
                                <a
                                  href={`https://wa.me/${waNumber}?text=${waMsg}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-semibold px-2.5 py-1 transition"
                                  data-testid={`wa-${r.tps_kode}-${p.username}`}
                                  title={`Chat WhatsApp ke ${p.name}`}
                                >
                                  <MessageCircle className="h-3 w-3" /> WA
                                </a>
                                <a
                                  href={`tel:${p.no_hp}`}
                                  className="inline-flex items-center gap-1 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-700 text-[11px] font-semibold px-2 py-1 transition"
                                  title={`Telepon ${p.name}`}
                                  data-testid={`tel-${r.tps_kode}-${p.username}`}
                                >
                                  <Phone className="h-3 w-3" />
                                </a>
                              </div>
                            ) : (
                              <span key={p.id} className="text-[10px] text-slate-400 italic">Nomor HP kosong</span>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      {hasAnomaly && (
                        <span className="inline-block rounded-full bg-red-100 text-red-700 text-[10px] px-2 py-0.5 font-semibold">
                          ANOMALI
                        </span>
                      )}
                      {!hasAnomaly && noPpdp && (
                        <span className="inline-block rounded-full bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 font-semibold">
                          BELUM DI-ASSIGN
                        </span>
                      )}
                      {!hasAnomaly && !noPpdp && (
                        <span className="inline-block rounded-full bg-emerald-100 text-emerald-800 text-[10px] px-2 py-0.5 font-semibold">
                          SIAP
                        </span>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50/50 border-t border-slate-100" data-testid={`detail-${r.tps_kode}`}>
                      <td colSpan={8} className="p-4">
                        <div className="grid gap-3 md:grid-cols-2">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                              Rincian Pemilih per RT
                            </div>
                            <div className="space-y-2">
                              {r.rw_breakdown.map((rw) => (
                                <div key={rw.rw} className="bg-white rounded border border-slate-200 p-2">
                                  <div className="text-xs font-semibold">RW {rw.rw} — {rw.subtotal.toLocaleString("id-ID")} pemilih</div>
                                  <div className="mt-1 grid grid-cols-4 gap-1 text-xs">
                                    {rw.rts.map((rt) => (
                                      <div key={rt.rt} className="bg-slate-50 rounded px-2 py-1 text-center">
                                        <div className="text-slate-500 text-[10px]">RT {rt.rt}</div>
                                        <div className="font-semibold tabular-nums">{rt.jumlah}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-3">
                            {r.anomalies.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-red-600 font-semibold mb-1">
                                  Anomali Data
                                </div>
                                <ul className="text-xs space-y-1">
                                  {r.anomalies.map((a, i) => (
                                    <li key={i} className="bg-red-50 border border-red-100 rounded px-2 py-1">
                                      {a.reason} → RW <b>{a.rw || "-"}</b>/RT <b>{a.rt || "-"}</b> ({a.count} pemilih)
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                                Rencana Assignment PPDP
                              </div>
                              <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2">
                                Setelah migrasi, satu PPDP akan di-assign ke <b>TPS {r.tps_kode}</b> ({r.dusun}) mencakup {r.jumlah_rw} RW & {r.jumlah_rt} RT ({r.total_pemilih.toLocaleString("id-ID")} pemilih).
                              </div>
                              {r.matching_ppdp.length > 0 && (
                                <div className="mt-2 text-xs">
                                  <span className="text-slate-500">PPDP kandidat (dari mapping RW lama):</span>
                                  {r.matching_ppdp.map((p) => (
                                    <div key={p.id} className="ml-2">• {p.name} <span className="text-slate-400">({p.username})</span></div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="p-6 text-center text-slate-400 text-sm">Tidak ada TPS yang cocok filter.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-slate-400 text-right">
        Generated at: {data.generated_at}
      </div>
    </div>
  );
}
